import { useCallback } from 'react';
import {
  streamAbeMessage,
  confirmAbeAction,
  clearAbeHistory,
  AbeConfirmPayload
} from '../api/abe';
import useAbeStore from '../store/zustand/abe';

const TOOL_LABELS: Record<string, string> = {
  assign_worker: 'Assigning worker…',
  draft_email: 'Drafting email…',
  flag_task: 'Flagging task…',
  generate_proposal: 'Generating proposal…',
  get_checklist_status: 'Checking checklist…',
  get_clients: 'Looking up your clients…',
  get_invoices: 'Reviewing invoices…',
  get_leads: 'Looking up leads…',
  get_outstanding_balance: 'Checking AR balances…',
  get_project_margin: 'Analyzing project margins…',
  get_projects: 'Checking project data…',
  get_prospects: 'Reviewing prospects…',
  get_schedule: 'Reviewing the schedule…',
  get_tasks: 'Checking tasks…',
  get_upcoming_jobs: 'Checking upcoming jobs…',
  get_workers: 'Looking up your team…',
  mark_invoice_paid: 'Processing payment…',
  reschedule_job: 'Rescheduling job…',
  run_sql: 'Running analysis…',
  send_invoice: 'Preparing invoice…',
  update_task_status: 'Updating task…'
};

export const useAbe = () => {
  const {
    abortController,
    addMessage,
    appendToMessage,
    clearMessages, // eslint-disable-line @typescript-eslint/no-unused-vars
    newSession,
    setAbortController,
    setPendingConfirmation,
    setStreaming,
    setThinkingStatus,
    updateMessage
  } = useAbeStore();

  const sendMessage = useCallback(
    async (userText: string) => {
      if (!userText.trim()) return;

      addMessage({ content: userText, role: 'user' });

      const assistantId = addMessage({
        content: '',
        isStreaming: true,
        role: 'assistant'
      });

      setStreaming(true);

      if (abortController) {
        abortController.abort();
      }

      const controller = new AbortController();
      setAbortController(controller);

      // Declared outside try so switch cases inside the callback can reference it
      let clientTimeout: ReturnType<typeof setTimeout> | undefined;

      try {
        let timedOut = false;
        clientTimeout = setTimeout(() => {
          timedOut = true;
          controller.abort();
          setThinkingStatus('');
          updateMessage(assistantId, {
            content:
              'That question took too long to answer. Try asking about a specific project, client, or time period instead.',
            isStreaming: false
          });
          setStreaming(false);
          setAbortController(null);
        }, 120_000);

        await streamAbeMessage(
          userText,
          (event) => {
            switch (event.type) {
              case 'confirmation_required':
                setPendingConfirmation(event.data as AbeConfirmPayload);
                updateMessage(assistantId, {
                  confirmationPayload: event.data,
                  requiresConfirmation: true
                });
                break;

              case 'done':
                clearTimeout(clientTimeout);
                setThinkingStatus('');
                updateMessage(assistantId, { isStreaming: false });
                setStreaming(false);
                setAbortController(null);
                break;

              case 'error':
                clearTimeout(clientTimeout);
                setThinkingStatus('');
                updateMessage(assistantId, {
                  content:
                    event.data.message ||
                    'Something went wrong. Please try again.',
                  isStreaming: false
                });
                setStreaming(false);
                setAbortController(null);
                break;

              case 'text':
                setThinkingStatus('');
                appendToMessage(assistantId, event.data.chunk ?? '');
                break;

              case 'tool_call': {
                const label =
                  event.data.tool === 'run_sql' && event.data.input?.description
                    ? `Analyzing: ${event.data.input.description}`
                    : TOOL_LABELS[event.data.tool] ?? 'Looking up data…';
                setThinkingStatus(label);
                break;
              }

              case 'tool_result':
                if (event.data.chartData) {
                  updateMessage(assistantId, {
                    chartData: event.data.chartData
                  });
                }
                break;

              default:
                break;
            }
          },
          controller.signal
        );
      } catch (err: any) {
        clearTimeout(clientTimeout);
        setThinkingStatus('');
        if (err?.name === 'AbortError') {
          updateMessage(assistantId, {
            content: '_(Response stopped)_',
            isStreaming: false
          });
        } else {
          updateMessage(assistantId, {
            content: 'Unable to reach Abe right now. Please try again.',
            isStreaming: false
          });
        }
        setStreaming(false);
        setAbortController(null);
      }
    },
    [
      abortController,
      addMessage,
      appendToMessage,
      setAbortController,
      setPendingConfirmation,
      setStreaming,
      setThinkingStatus,
      updateMessage
    ]
  );

  const confirmAction = useCallback(
    async (payload: AbeConfirmPayload) => {
      setPendingConfirmation(null);
      const confirmId = addMessage({
        content: '',
        isStreaming: true,
        role: 'assistant'
      });
      try {
        const result = await confirmAbeAction(payload.action, payload);
        updateMessage(confirmId, {
          content: result.success
            ? `✓ ${result.message}`
            : `Unable to complete: ${result.message}`,
          isStreaming: false
        });
      } catch {
        updateMessage(confirmId, {
          content: 'Action failed. Please try again.',
          isStreaming: false
        });
      }
    },
    [addMessage, setPendingConfirmation, updateMessage]
  );

  const stopStreaming = useCallback(() => {
    abortController?.abort();
    setThinkingStatus('');
    setStreaming(false);
    setAbortController(null);
  }, [abortController, setAbortController, setStreaming, setThinkingStatus]);

  const resetSession = useCallback(async () => {
    try {
      await clearAbeHistory();
    } catch {
      // Non-critical
    }
    newSession();
  }, [newSession]);

  return { confirmAction, resetSession, sendMessage, stopStreaming };
};

export default useAbe;
